import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ddtApi, clientsApi, productsApi, inboxApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useInbox } from '../context/InboxContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import ProductSearchDialog from '../components/ProductSearchDialog';
import ClientSearch from '../components/ClientSearch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trash2, Save, ArrowLeft, PackageSearch, Check, CloudOff, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const AUTOSAVE_DEBOUNCE_MS = 1500;

const DDTEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSlave, silentRefresh } = useAuth();
  const slave = isSlave();
  const maskMoney = (v) => slave ? '*****' : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Numero progressivo di "ordine" che viene assegnato alle nuove righe.
  // 1 di default; quando l'utente clicca "+ Ordine" diventa max(existing)+1
  // così le nuove righe risultano visivamente in un nuovo gruppo.
  const [currentOrderGroup, setCurrentOrderGroup] = useState(1);
  const [itemsFilter, setItemsFilter] = useState('');

  const [selectedClientId, setSelectedClientId] = useState('');
  const [items, setItems] = useState([]);
  const [listino, setListino] = useState('1');
  const [ddtInfo, setDdtInfo] = useState(null);
  const [formData, setFormData] = useState({
    causale: 'Vendita', aspetto_beni: 'A VISTA', porto: 'Franco',
    transport_date: '', transport_time: '', vettore: '', note: '', internal_ref: '', paid_amount: '', previous_balance: '',
  });

  // Autosave (PUT debounced)
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const saveTimerRef = useRef(null);
  const skipAutoSaveRef = useRef(true); // true durante il primo load, poi false

  // Vecchio segnato nell'ultimo DDT del cliente (per piccolo promemoria sotto il campo VECCHIO).
  // Resta null se non c'è storico o se il vecchio precedente è 0.
  const [lastDdtVecchio, setLastDdtVecchio] = useState(null);
  const [invoiced, setInvoiced] = useState(null);

  // Prezzi memorizzati per il cliente selezionato: { product_id: ultimo prezzo }
  const [clientPrices, setClientPrices] = useState({});
  const prevClientRef = useRef(null);

  useEffect(() => { fetchData(); }, [id]);

  useEffect(() => {
    if (!selectedClientId) { setClientPrices({}); return; }
    let cancelled = false;
    const isChange = !!prevClientRef.current && prevClientRef.current !== selectedClientId;
    prevClientRef.current = selectedClientId;
    clientsApi.productPrices(selectedClientId).then((res) => {
      if (cancelled) return;
      const map = Object.fromEntries(res.data.map(p => [p.product_id, p.price]));
      setClientPrices(map);
      if (isChange) repriceItemsForClient(map);
    }).catch(() => { if (!cancelled) setClientPrices({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // Auto-apri il ProductSearchDialog se arriviamo dalla lista DDT con ?focus=add
  // (bottone "Aggiungi un nuovo ordine a questo DDT"). Le nuove righe verranno
  // raggruppate in un nuovo `order_group` (esistente_max + 1) così risultano
  // visivamente separate nella tabella e nel PDF.
  useEffect(() => {
    if (loading) return;
    if (searchParams.get('focus') === 'add') {
      const maxGroup = items.reduce((m, it) => Math.max(m, parseInt(it.order_group) || 1), 1);
      setCurrentOrderGroup(maxGroup + 1);
      setSearchOpen(true);
      // Pulisci il param così se l'utente refresha non si riapre
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const fetchData = async () => {
    try {
      const [ddtRes, clientsRes, productsRes] = await Promise.all([
        ddtApi.getById(id), clientsApi.getAll(), productsApi.getAll(),
      ]);
      const ddt = ddtRes.data;
      setDdtInfo({ number: ddt.number, year: ddt.year });
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      setSelectedClientId(ddt.client_id);
      setInvoiced(ddt.invoice_id ? { id: ddt.invoice_id, number: ddt.invoice_number, year: ddt.invoice_year } : null);
      // Migra DDT vecchi senza order_group → default a 1
      const migrated = (ddt.items || []).map(it => ({ ...it, order_group: it.order_group || 1 }));
      setItems(migrated);
      // Allinea `currentOrderGroup` all'ultimo ordine presente: così aprire
      // il dialog dal bottone in alto continua nello stesso gruppo (non riparte da 1).
      const maxGroup = migrated.reduce((m, it) => Math.max(m, parseInt(it.order_group) || 1), 1);
      setCurrentOrderGroup(maxGroup);
      setFormData({
        causale: ddt.causale || 'Vendita',
        aspetto_beni: ddt.aspetto_beni || 'A VISTA',
        porto: ddt.porto || 'Franco',
        transport_date: ddt.transport_date || '',
        transport_time: ddt.transport_time || '',
        vettore: ddt.vettore || '',
        note: ddt.note || '',
        internal_ref: ddt.internal_ref || '',
        paid_amount: ddt.paid_amount?.toString() || '',
        previous_balance: ddt.previous_balance?.toString() || '',
      });
      // Riabilita l'autosave dopo che il fetch ha popolato lo state iniziale
      setTimeout(() => { skipAutoSaveRef.current = false; }, 400);
      // Storico: vecchio segnato sull'ultimo DDT del cliente (escludendo questo).
      if (ddt.client_id) {
        try {
          const sum = await clientsApi.lastDdtSummary(ddt.client_id, ddt.id);
          if (sum.data?.has_previous && sum.data.previous_balance && sum.data.previous_balance > 0) {
            setLastDdtVecchio({
              amount: sum.data.previous_balance,
              number: sum.data.ddt_number,
              year: sum.data.ddt_year,
              date: sum.data.ddt_date,
            });
          } else {
            setLastDdtVecchio(null);
          }
        } catch { setLastDdtVecchio(null); }
      }
    } catch (error) {
      toast.error('Errore nel caricamento del DDT');
      navigate('/ddt');
    } finally { setLoading(false); }
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);

  const priceForListino = (product, lis) => {
    if (lis === '2') return product.price_2 || product.price || 0;
    if (lis === '3') return product.price_3 || product.price || 0;
    return product.price || 0;
  };

  const addProductFromSearch = (product, boxQuantity = 1, explicitPieces) => {
    const boxes = Math.round((parseFloat(boxQuantity) || 0) * 100) / 100;
    if (boxes <= 0) return;
    const pieces_per_box = parseFloat(product.pieces_per_box) || 1;
    const isKg = (product.unit || 'pz') === 'kg';
    const pieces = (explicitPieces != null)
      ? (isKg ? (parseFloat(explicitPieces) || 0) : Math.round(parseFloat(explicitPieces) || 0))
      : (isKg ? boxes * pieces_per_box : Math.round(boxes * pieces_per_box));
    const price = clientPrices[product.id] ?? priceForListino(product, listino);
    const total = pieces * price;
    const total_cost = pieces * (product.cost || 0);
    const unitLabel = isKg ? 'kg' : 'pz';
    setItems(prev => [...prev, {
      product_id: product.id, product_code: product.code, description: product.description,
      boxes, pieces_per_box, pieces, price, cost: product.cost || 0, iva: product.iva || 22,
      total, total_cost, margin: total - total_cost, unit: unitLabel,
      order_group: currentOrderGroup,
    }]);
    toast.success(`${product.code} aggiunto (${boxes} box · ${pieces} ${unitLabel})`, { duration: 1500 });
  };

  // Cambio cliente: riprezza le righe con lo storico del nuovo cliente (o listino se assente).
  const repriceItemsForClient = (map) => {
    if (items.length === 0) return;
    setItems(prev => prev.map(it => {
      const product = products.find(p => p.id === it.product_id);
      const remembered = map[it.product_id];
      if (remembered == null && !product) return it;
      const price = remembered ?? priceForListino(product, listino);
      const pieces = parseFloat(it.pieces) || 0;
      const total = pieces * price;
      const total_cost = pieces * (parseFloat(it.cost) || 0);
      return { ...it, price, total, total_cost, margin: total - total_cost };
    }));
    toast.info('Prezzi aggiornati con lo storico del nuovo cliente');
  };

  const isClientPrice = (item) => {
    const remembered = clientPrices[item.product_id];
    return remembered != null && Math.abs((parseFloat(item.price) || 0) - remembered) < 0.005;
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    const item = newItems[index];
    const ppb = parseFloat(item.pieces_per_box) || 1;
    const boxes = parseFloat(item.boxes) || 0;
    const pieces = parseFloat(item.pieces) || 0;
    const isKg = item.unit === 'kg';
    const roundUnits = (v) => isKg ? (Math.round(v * 100) / 100) : Math.round(v);

    if (field === 'boxes') {
      item.pieces = roundUnits(boxes * ppb);
    } else if (field === 'pieces') {
      item.pieces = roundUnits(pieces);
      item.boxes = ppb > 0 ? Math.round((item.pieces / ppb) * 100) / 100 : 0;
    } else if (field === 'pieces_per_box') {
      item.pieces = roundUnits(boxes * ppb);
    }

    item.total = (parseFloat(item.pieces) || 0) * (parseFloat(item.price) || 0);
    item.total_cost = (parseFloat(item.pieces) || 0) * (parseFloat(item.cost) || 0);
    item.margin = item.total - item.total_cost;
    setItems(newItems);
  };

  // Naviga ↑/↓/Enter tra le righe della tabella prodotti (stesso campo)
  const handleItemKeyDown = (e, index, field) => {
    if (e.key === 'ArrowDown' || (e.key === 'Enter' && !e.shiftKey)) {
      const next = document.querySelector(`[data-row-field="${field}-${index + 1}"]`);
      if (next) { e.preventDefault(); next.focus(); next.select?.(); }
    } else if (e.key === 'ArrowUp') {
      const prev = document.querySelector(`[data-row-field="${field}-${index - 1}"]`);
      if (prev) { e.preventDefault(); prev.focus(); prev.select?.(); }
    }
  };

  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const calculateTotals = () => {
    const total_boxes = items.reduce((s, i) => s + (parseFloat(i.boxes) || 0), 0);
    const total_pieces = items.reduce((s, i) => s + (parseFloat(i.pieces) || 0), 0);
    const total_amount = items.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
    const total_cost = items.reduce((s, i) => s + (parseFloat(i.total_cost) || 0), 0);
    return { total_boxes, total_pieces, total_amount, total_cost, total_margin: total_amount - total_cost };
  };

  const totals = calculateTotals();
  const paid = parseFloat(formData.paid_amount) || 0;
  const prevBalance = parseFloat(formData.previous_balance) || 0;
  const grandTotal = totals.total_amount + prevBalance;
  const rawResto = grandTotal - paid;
  const resto = Math.abs(rawResto) < 0.01 ? 0 : rawResto;
  const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  // ===== AUTOSAVE (PUT debounced) =====
  // Ogni modifica a items/cliente/listino/formData triggera un salvataggio
  // automatico sul backend dopo AUTOSAVE_DEBOUNCE_MS ms. Niente più "draft":
  // il DDT è già confermato e l'autosave aggiorna la versione live.
  const buildPayload = useCallback(() => {
    if (!selectedClient) return null;
    return {
      client_id: selectedClient.id,
      client_name: (selectedClient.nickname && selectedClient.nickname.trim()) ? selectedClient.nickname : selectedClient.name,
      client_legal_name: selectedClient.name,
      client_address: selectedClient.address || '', client_city: selectedClient.city || '',
      client_cap: selectedClient.cap || '', client_piva: selectedClient.piva || '',
      items: items.map(it => ({
        product_id: it.product_id, product_code: it.product_code, description: it.description,
        boxes: parseFloat(it.boxes) || 0, pieces_per_box: parseFloat(it.pieces_per_box) || 0,
        pieces: parseFloat(it.pieces) || 0, price: parseFloat(it.price) || 0,
        cost: parseFloat(it.cost) || 0, iva: parseFloat(it.iva) || 22,
        total: parseFloat(it.total) || 0, total_cost: parseFloat(it.total_cost) || 0,
        margin: parseFloat(it.margin) || 0,
        unit: it.unit || 'pz',
        order_group: parseInt(it.order_group) || 1,
      })),
      total_boxes: totals.total_boxes, total_pieces: totals.total_pieces,
      total_amount: totals.total_amount, total_cost: totals.total_cost,
      total_margin: totals.total_margin, paid_amount: paid,
      causale: formData.causale, aspetto_beni: formData.aspetto_beni, porto: formData.porto,
      transport_date: formData.transport_date, transport_time: formData.transport_time,
      vettore: formData.vettore, note: formData.note, internal_ref: formData.internal_ref,
      previous_balance: prevBalance,
    };
  }, [selectedClient, items, totals, formData, paid, prevBalance]);

  useEffect(() => {
    if (loading || skipAutoSaveRef.current || invoiced) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const payload = buildPayload();
      if (!payload) return;
      try {
        setAutoSaveStatus('saving');
        await ddtApi.update(id, payload);
        setAutoSaveStatus('saved');
      } catch (err) {
        const status = err?.response?.status;
        // Sessione scaduta: proviamo un silent refresh e ri-salviamo UNA volta sola.
        if ((status === 401 || status === 403) && silentRefresh) {
          const ok = await silentRefresh();
          if (ok) {
            try {
              await ddtApi.update(id, payload);
              setAutoSaveStatus('saved');
              return;
            } catch (e2) {
              const s2 = e2?.response?.status;
              if (s2 === 401 || s2 === 403) {
                setAutoSaveStatus('error');
                toast.error('Sessione scaduta: accedi di nuovo per salvare — i dati nella pagina restano fino al refresh.');
                return;
              }
              // network error dopo refresh: tratta come errore normale (no data loss, no logout)
              setAutoSaveStatus('error');
              return;
            }
          } else {
            setAutoSaveStatus('error');
            toast.error('Sessione scaduta: accedi di nuovo per salvare — i dati nella pagina restano fino al refresh.');
            return;
          }
        }
        // Errore di rete / server (NO logout): mostra solo stato errore. L'utente ritenterà alla prossima modifica.
        setAutoSaveStatus('error');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [loading, buildPayload, id, silentRefresh]);

  const autoSaveLabel = {
    idle: '',
    saving: 'Salvataggio…',
    saved: 'Salvato',
    error: 'Errore salvataggio',
  }[autoSaveStatus];

  const autoSaveColor = {
    idle: 'text-muted-foreground',
    saving: 'text-blue-600',
    saved: 'text-emerald-600',
    error: 'text-red-600',
  }[autoSaveStatus];

  const AutoSaveIcon = autoSaveStatus === 'saving' ? Loader2 : autoSaveStatus === 'saved' ? Check : autoSaveStatus === 'error' ? CloudOff : null;

  // === Suggerimento invio automatico post-save (match per P.IVA) ===
  const [autoSendOpen, setAutoSendOpen] = useState(false);
  const [autoSendTarget, setAutoSendTarget] = useState(null); // {id, name}
  const [autoSending, setAutoSending] = useState(false);
  const { refresh: refreshInboxBadge } = useInbox();

  const confirmAutoSend = async () => {
    if (!autoSendTarget) return;
    setAutoSending(true);
    try {
      await inboxApi.send(id, autoSendTarget.id);
      toast.success(`DDT inviato a ${autoSendTarget.name}`);
      refreshInboxBadge();
      setAutoSendOpen(false);
      navigate(`/ddt/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio");
    } finally {
      setAutoSending(false);
    }
  };

  const dismissAutoSend = () => {
    setAutoSendOpen(false);
    navigate(`/ddt/${id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) { toast.error('Seleziona un cliente'); return; }
    // Flush eventuale autosave pendente prima del save esplicito
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const ddtData = {
      client_id: selectedClient.id,
      client_name: (selectedClient.nickname && selectedClient.nickname.trim()) ? selectedClient.nickname : selectedClient.name,
      client_legal_name: selectedClient.name,
      client_address: selectedClient.address || '', client_city: selectedClient.city || '',
      client_cap: selectedClient.cap || '', client_piva: selectedClient.piva || '',
      items: items.map(it => ({
        product_id: it.product_id, product_code: it.product_code, description: it.description,
        boxes: parseFloat(it.boxes) || 0, pieces_per_box: parseFloat(it.pieces_per_box) || 0,
        pieces: parseFloat(it.pieces) || 0, price: parseFloat(it.price) || 0,
        cost: parseFloat(it.cost) || 0, iva: parseFloat(it.iva) || 22,
        total: parseFloat(it.total) || 0, total_cost: parseFloat(it.total_cost) || 0,
        margin: parseFloat(it.margin) || 0,
        unit: it.unit || 'pz',
        order_group: parseInt(it.order_group) || 1,
      })),
      total_boxes: totals.total_boxes, total_pieces: totals.total_pieces,
      total_amount: totals.total_amount, total_cost: totals.total_cost,
      total_margin: totals.total_margin, paid_amount: paid,
      causale: formData.causale, aspetto_beni: formData.aspetto_beni, porto: formData.porto,
      transport_date: formData.transport_date, transport_time: formData.transport_time,
      vettore: formData.vettore, note: formData.note, internal_ref: formData.internal_ref,
      previous_balance: prevBalance,
    };
    setSaving(true);
    try {
      await ddtApi.update(id, ddtData);
      toast.success('DDT aggiornato');
      // Prova il lookup per P.IVA: se il cliente coincide con un'azienda registrata,
      // apri il dialog di conferma invio automatico. Altrimenti naviga subito al view.
      const piva = (selectedClient.piva || '').trim();
      if (piva) {
        try {
          const lk = await inboxApi.lookupByPiva(piva);
          if (lk.data?.found && lk.data?.company?.id) {
            setAutoSendTarget(lk.data.company);
            setAutoSendOpen(true);
            setSaving(false);
            return; // fermati qui, naviga solo dopo che l'utente decide
          }
        } catch { /* lookup best-effort, non blocca */ }
      }
      navigate(`/ddt/${id}`);
    } catch (error) {
      toast.error('Errore aggiornamento');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Caricamento...</div>;

  return (
    <div data-testid="ddt-edit-page" className="space-y-4 max-w-6xl mx-auto">
      {invoiced && (
        <div data-testid="ddt-invoiced-banner" className="p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm">
          Questo DDT è già incluso nella fattura N. {invoiced.number}/{invoiced.year}: le modifiche non vengono salvate.{' '}
          <button type="button" className="underline font-semibold" onClick={() => navigate(`/fatture/${invoiced.id}`)}>Apri la fattura</button>
        </div>
      )}
      <ProductSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        products={products}
        onInsert={addProductFromSearch}
        existingItems={items}
        allowDuplicates={currentOrderGroup > 1}
      />
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/ddt/${id}`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Modifica DDT n° {ddtInfo?.number}-{ddtInfo?.year}</h1>
          <p className="text-muted-foreground text-sm">{invoiced ? 'DDT fatturato — sola lettura' : 'Le modifiche vengono salvate automaticamente'}</p>
        </div>
        {AutoSaveIcon && (
          <div
            data-testid="autosave-status"
            className={`inline-flex items-center gap-1.5 text-xs font-medium ${autoSaveColor}`}
          >
            <AutoSaveIcon className={`w-3.5 h-3.5 ${autoSaveStatus === 'saving' ? 'animate-spin' : ''}`} />
            {autoSaveLabel}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="card-swiss">
          <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Destinatario</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-3">
            <ClientSearch clients={clients} value={selectedClientId} onChange={setSelectedClientId} />
            <div className="flex items-center gap-3">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Listino prezzi</Label>
              <Select value={listino} onValueChange={setListino}>
                <SelectTrigger data-testid="listino-select" className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Listino 1 (Standard)</SelectItem>
                  <SelectItem value="2">Listino 2 (Ingrosso)</SelectItem>
                  <SelectItem value="3">Listino 3 (Speciale)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-semibold">Prodotti</CardTitle>
              <Button
                type="button"
                onClick={() => setSearchOpen(true)}
                data-testid="open-product-search-btn"
                className="h-9 bg-green-600 hover:bg-green-700 text-white"
              >
                <PackageSearch className="w-4 h-4 mr-2" />
                Cerca e Aggiungi Prodotti
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {items.length > 0 ? (
              <>
                {items.length > 3 && (
                  <div className="mb-3 relative">
                    <Input
                      data-testid="items-filter-input"
                      placeholder="Filtra prodotti già inseriti per codice o descrizione..."
                      value={itemsFilter}
                      onChange={(e) => setItemsFilter(e.target.value)}
                      className="h-9 text-sm pl-8"
                    />
                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 18a7 7 0 110-14 7 7 0 010 14z"/></svg>
                  </div>
                )}
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[36px] text-center">#</TableHead>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[80px]">Codice</TableHead>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-2">Descrizione</TableHead>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[70px] text-center">Box</TableHead>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[55px] text-center">Pz/B</TableHead>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[70px] text-center">Pezzi</TableHead>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[80px] text-right">Prezzo</TableHead>
                    <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[95px] text-right">Totale</TableHead>
                    <TableHead className="h-8 w-[32px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, index) => {
                    if (itemsFilter.trim()) {
                      const q = itemsFilter.trim().toLowerCase();
                      const hay = ((item.product_code || '') + ' ' + (item.description || '')).toLowerCase();
                      if (!hay.includes(q)) return null;
                    }
                    // Separatore visivo tra ordini diversi (order_group)
                    const currentGroup = parseInt(item.order_group) || 1;
                    const prevGroup = index > 0 ? (parseInt(items[index - 1].order_group) || 1) : null;
                    const showGroupSeparator = prevGroup !== null && currentGroup !== prevGroup;
                    const groupColors = {
                      1: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
                      2: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
                      3: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
                      4: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
                      5: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
                    };
                    const palette = groupColors[currentGroup] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
                    return (
                    <React.Fragment key={index}>
                      {showGroupSeparator && (
                        <TableRow data-testid={`order-separator-${currentGroup}`}>
                          <TableCell colSpan={11} className={`py-1.5 px-3 border-t-2 ${palette.border} ${palette.bg}`}>
                            <span className={`text-[10px] uppercase tracking-wider font-bold ${palette.text}`}>
                              📦 Ordine {currentGroup}
                            </span>
                          </TableCell>
                        </TableRow>
                      )}
                    <TableRow className={`hover:bg-muted/30 ${currentGroup > 1 ? `border-l-4 ${palette.border}` : ''}`}>
                      <TableCell className="py-1 px-2 text-sm font-['JetBrains_Mono'] font-semibold text-center text-muted-foreground" data-testid={`item-row-num-${index}`}>{index + 1}</TableCell>
                      <TableCell className="py-1 px-2 text-sm font-['JetBrains_Mono'] font-semibold">{item.product_code}</TableCell>
                      <TableCell className="py-1 px-2">
                        <Input data-testid={`item-description-${index}`} data-row-field={`description-${index}`} value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} onKeyDown={(e) => handleItemKeyDown(e, index, 'description')} className="h-8 text-sm" />
                      </TableCell>
                      <TableCell className="py-1 px-1">
                        <Input data-testid={`item-boxes-${index}`} data-row-field={`boxes-${index}`} type="number" step="any" min="0" value={item.boxes} onChange={(e) => updateItem(index, 'boxes', e.target.value)} onKeyDown={(e) => handleItemKeyDown(e, index, 'boxes')} className="cell-input h-8 text-center font-['JetBrains_Mono'] text-sm px-1 font-semibold" />
                      </TableCell>
                      <TableCell className="py-1 px-1">
                        <Input data-testid={`item-pieces-per-box-${index}`} data-row-field={`pieces_per_box-${index}`} type="number" step="any" min="0" value={item.pieces_per_box} onChange={(e) => updateItem(index, 'pieces_per_box', e.target.value)} onKeyDown={(e) => handleItemKeyDown(e, index, 'pieces_per_box')} className="h-8 text-center font-['JetBrains_Mono'] text-sm px-1" />
                      </TableCell>
                      <TableCell className="py-1 px-1">
                        <div className="relative">
                          <Input data-testid={`item-pieces-${index}`} data-row-field={`pieces-${index}`} type="number" step="any" min="0" value={item.pieces} onChange={(e) => updateItem(index, 'pieces', e.target.value)} onKeyDown={(e) => handleItemKeyDown(e, index, 'pieces')} className="cell-input h-8 text-center font-['JetBrains_Mono'] text-sm px-1 font-semibold pr-7" />
                          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] uppercase font-bold text-muted-foreground pointer-events-none">
                            {item.unit === 'kg' ? 'kg' : 'pz'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-1 px-1">
                        <Input data-testid={`item-price-${index}`} data-row-field={`price-${index}`} type="number" step="0.01" value={item.price} onChange={(e) => updateItem(index, 'price', e.target.value)} onKeyDown={(e) => handleItemKeyDown(e, index, 'price')} className="h-8 text-right font-['JetBrains_Mono'] text-sm px-1 font-semibold" />
                        {isClientPrice(item) && (() => {
                          const prod = products.find(p => p.id === item.product_id);
                          return (
                            <div data-testid={`item-client-price-badge-${index}`} className="mt-0.5 text-[10px] leading-tight text-right text-emerald-700 dark:text-emerald-400 font-medium whitespace-nowrap">
                              Ultimo prezzo cliente
                              {prod && <span data-testid={`item-listino-price-${index}`} className="text-muted-foreground font-normal"> · List. €{Number(priceForListino(prod, listino)).toFixed(2)}</span>}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="py-1 px-2 text-right font-['JetBrains_Mono'] font-bold text-sm">{formatCurrency(item.total)}</TableCell>
                      <TableCell className="py-1 px-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)} data-testid={`remove-item-${index}`} className="h-7 w-7 text-destructive">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );})}
                </TableBody>
              </Table>
              </>
            ) : (
              <div className="text-center py-10 text-muted-foreground text-sm">Aggiungi un prodotto</div>
            )}
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardContent className="py-4">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Riepilogo</p>
                <div className="flex justify-between text-xs"><span>Totale Box</span><span className="font-['JetBrains_Mono']">{totals.total_boxes}</span></div>
                <div className="flex justify-between text-xs"><span>Totale Pezzi</span><span className="font-['JetBrains_Mono']">{totals.total_pieces}</span></div>
                <div className="flex justify-between items-center pt-1 border-t border-border">
                  <span className="font-semibold">TOTALE PRODOTTI</span>
                  <span className="font-['JetBrains_Mono'] font-bold text-lg">{formatCurrency(totals.total_amount)}</span>
                </div>
                {prevBalance > 0 && (
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-amber-700 font-semibold">+ VECCHIO</span>
                    <span className="font-['JetBrains_Mono'] font-semibold text-amber-700">{formatCurrency(prevBalance)}</span>
                  </div>
                )}
                {prevBalance > 0 && (
                  <div className="flex justify-between items-center pt-1 border-t border-border">
                    <span className="font-bold">TOTALE A PAGARE</span>
                    <span className="font-['JetBrains_Mono'] font-bold text-lg">{formatCurrency(grandTotal)}</span>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pagamento &amp; Vecchio</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px]">Acconto pagato (€)</Label>
                    <Input data-testid="paid-amount-input" type="number" step="0.01" min="0" value={formData.paid_amount} onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })} placeholder="0,00" className="font-['JetBrains_Mono'] h-9" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-amber-700 font-semibold">VECCHIO (€)</Label>
                    {lastDdtVecchio && (
                      <div
                        data-testid="last-ddt-vecchio-hint"
                        className="text-[10px] text-amber-700/80 leading-tight mb-0.5"
                      >
                        Ultimo DDT n°{lastDdtVecchio.number}/{lastDdtVecchio.year}: vecchio {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(lastDdtVecchio.amount)}
                      </div>
                    )}
                    <Input
                      data-testid="previous-balance-input"
                      type="number" step="0.01"
                      value={formData.previous_balance}
                      onChange={(e) => setFormData({ ...formData, previous_balance: e.target.value })}
                      placeholder="0,00"
                      className="font-['JetBrains_Mono'] h-9 border-amber-300 focus-visible:ring-amber-400"
                      title="Importo VECCHIO. Solo informativo sul PDF, NON entra in fatturato/guadagno."
                    />
                  </div>
                </div>
                <p className="text-[9px] text-muted-foreground leading-tight">
                  &quot;VECCHIO&quot; appare sul PDF ma non viene contato come guadagno.
                </p>
                <div className="flex justify-between text-xs pt-1">
                  <span className="text-muted-foreground">Resto da pagare</span>
                  <span className={`font-['JetBrains_Mono'] font-semibold ${resto > 0 ? 'text-orange-600' : 'text-green-600'}`}>{formatCurrency(resto)}</span>
                </div>
              </div>
              <div className="space-y-1.5 bg-green-50/50 -m-4 p-4 rounded-sm">
                <p className="text-[10px] uppercase tracking-wider text-green-700">Solo per te (privato)</p>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Costo</span><span className="font-['JetBrains_Mono']">{maskMoney(totals.total_cost)}</span></div>
                <div className="flex justify-between items-center pt-1 border-t border-green-200/50">
                  <span className="font-semibold text-green-700">GUADAGNO</span>
                  <span className="font-['JetBrains_Mono'] font-bold text-green-700">{maskMoney(totals.total_margin)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Informazioni Trasporto</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Causale</Label><Input value={formData.causale} onChange={(e) => setFormData({ ...formData, causale: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Aspetto Beni</Label><Input value={formData.aspetto_beni} onChange={(e) => setFormData({ ...formData, aspetto_beni: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Porto</Label>
                <Select value={formData.porto} onValueChange={(v) => setFormData({ ...formData, porto: v })}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Franco">Franco</SelectItem>
                    <SelectItem value="Assegnato">Assegnato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Data Trasporto</Label><Input type="date" value={formData.transport_date} onChange={(e) => setFormData({ ...formData, transport_date: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ora Trasporto</Label><Input type="time" value={formData.transport_time} onChange={(e) => setFormData({ ...formData, transport_time: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vettore</Label><Input value={formData.vettore} onChange={(e) => setFormData({ ...formData, vettore: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Riferimento Interno</Label><Input value={formData.internal_ref} onChange={(e) => setFormData({ ...formData, internal_ref: e.target.value })} className="h-9 text-sm" /></div>
              <div className="md:col-span-2 space-y-1"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Note</Label><Textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={2} className="text-sm" /></div>
            </div>
          </CardContent>
        </Card>

        <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border py-3 -mx-6 lg:-mx-8 px-6 lg:px-8 z-10">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="text-sm">Totale: <span className="font-bold font-['JetBrains_Mono']">{formatCurrency(totals.total_amount)}</span>
              {paid > 0 && <span className="ml-3 text-muted-foreground">Resto: <span className="font-['JetBrains_Mono']">{formatCurrency(resto)}</span></span>}
            </div>
            <Button type="submit" data-testid="save-ddt-btn" disabled={saving || !selectedClient || items.length === 0 || Boolean(invoiced)} className={`btn-primary ${invoiced ? 'hidden' : ''}`}>
              <Save className="w-4 h-4 mr-2" />{saving ? 'Salvataggio...' : 'AGGIORNA DDT'}
            </Button>
          </div>
        </div>
      </form>

      {/* Dialog "Invio automatico" — compare dopo SALVA se la P.IVA del cliente
          corrisponde a un'azienda registrata sulla piattaforma. */}
      <Dialog open={autoSendOpen} onOpenChange={(v) => { if (!v) dismissAutoSend(); }}>
        <DialogContent data-testid="auto-send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-600" />
              Inviare subito questo DDT?
            </DialogTitle>
            <DialogDescription>
              Il cliente di questo DDT ha la stessa <b>Partita IVA</b> dell'azienda{' '}
              <b data-testid="auto-send-target-name">{autoSendTarget?.name}</b> registrata su DDT Manager.
              Vuoi inviargli il DDT? Lo riceverà subito nel suo <b>Inbox</b>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={dismissAutoSend}
              data-testid="auto-send-dismiss-btn"
              disabled={autoSending}
            >
              No, grazie
            </Button>
            <Button
              onClick={confirmAutoSend}
              data-testid="auto-send-confirm-btn"
              disabled={autoSending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {autoSending ? 'Invio…' : `Invia a ${autoSendTarget?.name || 'destinatario'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DDTEditPage;
